/**
 * 10월 PRO 최대 50% 이벤트 (/events/pro-50, 캠페인 pro50_2026q4) 단일 소스.
 *
 * - 기간: 2026-10-01 00:00:00 ~ 2026-10-31 23:59:59 KST (시각 기준 자동 전환)
 * - 인원 제한 없음: 개인 결제와 단체 결제 모두 1명부터
 * - 플랜별 차등: 12개월 50% / 6개월 40% / 3개월 30% / 1개월 20%
 * - 할인은 중복되지 않는다: 쿠폰·인원 할인·이벤트 중 가장 큰 할인 하나만
 *
 * backend/config/pro50_campaign.py 와 값이 같아야 한다. 결제 금액은 서버가 같은 규칙으로
 * 다시 계산해 검증하므로 여기 값은 표시용이다.
 * 긴급 종료: PRO50_EVENT_ENABLED=false (서버는 PRO50_EVENT_ENABLED 환경변수로 별도 차단)
 */

const KST = '+09:00';

export const PRO50_EVENT_ENABLED = true;
export const PRO50_EVENT_START = `2026-10-01T00:00:00${KST}`;
export const PRO50_EVENT_END = `2026-10-31T23:59:59${KST}`;
export const PRO50_EVENT_END_LABEL = '10월 31일까지';
export const PRO50_EVENT_END_SHORT = '10.31(토)';
export const PRO50_COUPON_CODE = 'PRO50OCT';
export const PRO50_PROMO_SLUG = 'pro50';
export const PRO50_CAMPAIGN = 'pro50_2026q4';
// 랜딩 신청 폼 eventType (backend config/pro50_campaign.PRO50_APPLY_EVENT_TYPE)
export const PRO50_APPLY_EVENT_TYPE = 'pro50_2026q4';
export const PRO50_LANDING_PATH = '/events/pro-50';
export const PRO50_PRICING_POLICY = 'event_pro50';
export const PRO50_TIER_LABEL = '10월 이벤트';
export const PRO50_QUOTE_HONOR_DAYS = 30;

// 9월 단체 50% 이벤트 종료 시각과, 9월 견적서 보호(promo=group50 + 10명 이상) 종료 시각
export const GROUP50_EVENT_END = `2026-09-30T23:59:59${KST}`;
export const GROUP50_LEGACY_HONOR_END = `2026-10-30T23:59:59${KST}`;

export const PRO50_BASE_PRICES = {
  '1month': 24000,
  '3month': 60000,
  '6month': 110000,
  '12month': 200000,
};

export const PRO50_DISCOUNT_RATES = {
  '1month': 0.2,
  '3month': 0.3,
  '6month': 0.4,
  '12month': 0.5,
};

export const PRO50_PRICES = Object.fromEntries(
  Object.entries(PRO50_DISCOUNT_RATES).map(([planId, rate]) => [
    planId,
    Math.round(PRO50_BASE_PRICES[planId] * (1 - rate)),
  ])
);

export const PRO50_PLAN_ORDER = ['12month', '6month', '3month', '1month'];
export const PRO50_PLAN_LABELS = {
  '1month': '1개월',
  '3month': '3개월',
  '6month': '6개월',
  '12month': '12개월',
};
export const PRO50_PLAN_MONTHS = { '1month': 1, '3month': 3, '6month': 6, '12month': 12 };

const PRODUCTION_HOSTS = ['whatquiz.co.kr', 'www.whatquiz.co.kr'];
export const PRO50_PREVIEW_KEY = 'pro50_preview';

// 로컬·테스트 도메인에서만 localStorage 'pro50_preview'='1' 로 10월 상태를 미리 본다.
export const isPro50Preview = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (PRODUCTION_HOSTS.includes(window.location.hostname)) return false;
    return window.localStorage.getItem(PRO50_PREVIEW_KEY) === '1';
  } catch {
    return false;
  }
};

const toMs = (now) => (now instanceof Date ? now.getTime() : new Date(now).getTime());

export const isPro50Active = (now = new Date()) => {
  if (!PRO50_EVENT_ENABLED) return false;
  const testOverride = typeof window !== 'undefined' ? window.__PRO50_TEST_ACTIVE__ : undefined;
  if (typeof testOverride === 'boolean') return testOverride;
  if (isPro50Preview()) return true;
  const ts = toMs(now);
  return ts >= new Date(PRO50_EVENT_START).getTime() && ts <= new Date(PRO50_EVENT_END).getTime();
};

/** 시작 전(9/30까지)이면 true — 랜딩은 열어 두되 신청·결제는 막는다. 종료 후는 false */
export const isPro50Upcoming = (now = new Date()) => {
  if (!PRO50_EVENT_ENABLED || isPro50Preview()) return false;
  if (typeof window !== 'undefined') {
    if (typeof window.__PRO50_TEST_UPCOMING__ === 'boolean') return window.__PRO50_TEST_UPCOMING__;
    if (typeof window.__PRO50_TEST_ACTIVE__ === 'boolean') return false;
  }
  return toMs(now) < new Date(PRO50_EVENT_START).getTime();
};

export const PRO50_START_LABEL = '10월 1일(목) 0시';

export const isGroup50Period = (now = new Date()) => {
  if (isPro50Preview()) return false;
  return toMs(now) <= new Date(GROUP50_EVENT_END).getTime();
};

export const isGroup50LegacyHonorPeriod = (now = new Date()) => (
  toMs(now) <= new Date(GROUP50_LEGACY_HONOR_END).getTime()
);

export const normalizePro50PlanId = (planId) => String(planId || '').trim().toLowerCase().replace('pro_', '');

/** 이벤트 기간이면 플랜 이벤트가, 아니면 null */
export const getPro50Price = (planId, now = new Date()) => {
  if (!isPro50Active(now)) return null;
  return PRO50_PRICES[normalizePro50PlanId(planId)] ?? null;
};

export const getPro50DiscountPercent = (planId) => (
  Math.round((PRO50_DISCOUNT_RATES[normalizePro50PlanId(planId)] || 0) * 100)
);

/** 이미 계산된 가격에 이벤트가를 겹친다 — 둘 중 싼 쪽 (중복 할인 없음) */
export const applyPro50 = (planId, price, now = new Date()) => {
  const eventPrice = getPro50Price(planId, now);
  return eventPrice == null ? price : Math.min(price, eventPrice);
};

export const getPro50MonthlyPrice = (planId) => {
  const key = normalizePro50PlanId(planId);
  return Math.round(PRO50_PRICES[key] / PRO50_PLAN_MONTHS[key]);
};

export const getPro50PricingRows = () => PRO50_PLAN_ORDER.map((planId) => ({
  planId,
  label: PRO50_PLAN_LABELS[planId],
  basePrice: PRO50_BASE_PRICES[planId],
  eventPrice: PRO50_PRICES[planId],
  discountPercent: getPro50DiscountPercent(planId),
  monthlyPrice: getPro50MonthlyPrice(planId),
}));

export const PRO50_ENTRY_PARAM = 'from';
export const buildPro50LandingUrl = (entry = '') => {
  const token = String(entry || '').trim().slice(0, 40).replace(/[^a-zA-Z0-9._-]/g, '_');
  return token ? `${PRO50_LANDING_PATH}?${PRO50_ENTRY_PARAM}=${token}` : PRO50_LANDING_PATH;
};

export const buildPro50CheckoutUrl = (planId = '12month') => (
  `/subscription/pro?plan=${normalizePro50PlanId(planId) || '12month'}&promo=${PRO50_PROMO_SLUG}`
);

export const buildPro50BulkUrl = (planId = '12month', quantity = 1) => (
  `/bulk-purchase?promo=${PRO50_PROMO_SLUG}&plan=${normalizePro50PlanId(planId) || '12month'}&qty=${Math.max(1, Number(quantity) || 1)}`
);

export const buildPro50QuotationUrl = (planId = '12month', quantity = 1) => (
  `${buildPro50BulkUrl(planId, quantity)}&intent=quote`
);

// 광고 유입은 대부분 인스타 인앱 브라우저라 로그인이 막히기 쉽다. 그래서 로그인 없는 단체 결제 화면(1명부터)을 주 버튼으로 쓴다.
export const PRO50_ANGLE_PARAM = 'angle';
export const isPro50PaidEntry = (search = '') => {
  const params = new URLSearchParams(search);
  if (params.get(PRO50_ANGLE_PARAM)) return true;
  return String(params.get('utm_medium') || '').trim().toLowerCase() === 'paid_social';
};

export const PRO50_ANGLES = ['deadline', 'price', 'season', 'confess', 'budget', 'grading', 'record', 'rtg'];
export const getPro50Angle = (search = '') => {
  const value = String(new URLSearchParams(search).get(PRO50_ANGLE_PARAM) || '').trim().toLowerCase();
  return PRO50_ANGLES.includes(value) ? value : null;
};

export const PRO50_ENTRY_SEEN_KEY = 'pro50_entry_seen';
export const hasSeenPro50Entry = () => {
  try {
    return localStorage.getItem(PRO50_ENTRY_SEEN_KEY) === '1';
  } catch {
    return true;
  }
};
export const markPro50EntrySeen = () => {
  try {
    localStorage.setItem(PRO50_ENTRY_SEEN_KEY, '1');
  } catch { /* 저장 실패는 무시 */ }
};

export const isPro50PromoSlug = (value) => String(value || '').trim().toLowerCase() === PRO50_PROMO_SLUG;
